/**
 * The briefing-v1 data contract, in TypeScript.
 *
 * `briefing.schema.json` beside this file is the canonical definition and the one
 * the backend validates against — this file mirrors it. Change both together, or
 * the UI will type-check against a shape the generator never produces.
 */

export type Provenance = 'public' | 'relationship' | 'inference';
/** Severity of a cell's own word (Strong / High / Owed by Oct 24). The data says
 *  how serious it is; the stylesheet picks the ink. */
export type Emphasis = 'positive' | 'caution' | 'risk';
export type SectionId = 'q1' | 'q2' | 'q3' | 'q4' | 'q5' | 'q6' | 'q7';
export type SectionStatus = 'generated' | 'edited' | 'stale' | 'error';

export interface Prose {
  text: string;
  provenance?: Provenance;
  source_ids?: string[];
}

export interface BlockBullets { type: 'bullets'; items: Prose[]; }
export interface TableCell extends Prose { emphasis?: Emphasis; }
export interface BlockTable {
  type: 'table';
  columns: string[];
  rows: TableCell[][];
  provenance?: Provenance;
  source_ids?: string[];
}
export interface Step {
  label: string;
  date?: string;
  state: 'done' | 'now' | 'next';
  emphasis?: Emphasis;
}
export interface BlockSteps { type: 'steps'; items: Step[]; }
export interface KvItem extends Omit<Prose, 'text'> { key: string; value: string; }
export interface BlockKV { type: 'kv'; items: KvItem[]; }
export interface Tile {
  name: string;
  why: string;
  meta?: string;
  facts: Prose[];
  angle: Prose;
}
export interface BlockTiles { type: 'tiles'; items: Tile[]; }
export interface Priority extends Prose { owner?: string; date?: string; }
export interface BlockPriorities { type: 'priorities'; items: Priority[]; }
export interface BlockText { type: 'text'; lead?: string; body: Prose; }
export interface BlockExhibitRef { type: 'exhibit_ref'; exhibit_id: string; }

export type Block =
  | BlockBullets | BlockTable | BlockSteps | BlockKV
  | BlockTiles | BlockPriorities | BlockText | BlockExhibitRef;

export interface PartnershipNode {
  id: string;
  label: string;
  meta?: string[];
  side: 'them' | 'us';
  proposed?: boolean;
}
export interface PartnershipFlow {
  from: string;
  to: string;
  label?: string;
  kind: 'commitment' | 'return' | 'proposed';
}
export interface PartnershipData { nodes: PartnershipNode[]; flows: PartnershipFlow[]; }

export interface TimelinePoint { date: string; value: number; }
export interface TimelineEvent { date: string; label: string; }
export interface TimelineData {
  points: TimelinePoint[];
  events?: TimelineEvent[];
  max?: number;
  unit?: string;
  callout?: string;
}

export interface Exhibit {
  id: string;
  kind: 'partnership' | 'timeline';
  label?: string;
  title: string;
  note?: Prose;
  data: PartnershipData | TimelineData;
  source_ids?: string[];
}

export interface SectionNote { text: string; kind?: 'evidence' | 'flag'; }

export interface Section {
  id: SectionId;
  question: string;
  headline: string;
  note?: SectionNote;
  blocks: Block[];
  status: SectionStatus;
  edited_by?: string | null;
  edited_at?: string | null;
}

export interface Kpi {
  value: string;
  label: string;
  provenance?: Provenance;
  source_ids?: string[];
}
export interface Flag { label: string; detail?: string; section: SectionId; }

export interface BriefingSummary {
  headline: string;
  dek: Prose;
  kpis: Kpi[];
  flags: Flag[];
  /** The summary is overridden under the reserved id `summary`, so it carries the
   *  same edit state a section does. */
  status?: 'generated' | 'edited' | 'stale';
  edited_by?: string | null;
  edited_at?: string | null;
}

export interface Basis { threads: number; meetings: number; notes: number; public_sources: number; }

export interface Source {
  id: string;
  kind: 'public' | 'email' | 'meeting' | 'note' | 'document';
  label: string;
  url?: string;
  message_id?: string;
  owner_email?: string;
  event_id?: string;
  note_id?: string;
  document_id?: string;
}

export interface Briefing {
  briefing_id: string;
  version: number;
  counterparty_id: string;
  company: string;
  generated_at: string;
  model_id: string;
  prompt_version: string;
  basis: Basis;
  summary: BriefingSummary;
  exhibits: Exhibit[];
  sections: Section[];
  sources: Source[];
}

/** What a section override carries. Mirrors `fund_briefing_overrides.content_json`:
 *  structured content, never HTML — the contract has no rich-text field, and a
 *  team-wide briefing renders one person's edit in everyone else's browser. */
export interface SectionOverride {
  headline: string;
  blocks: Block[];
}

/** The summary is edited with the same endpoint, under the reserved id below, so
 *  the backend needs one override table rather than two. */
export const SUMMARY_OVERRIDE_ID = 'summary';
export interface SummaryOverride { headline: string; dek: string; }

/** Everything the block components need that is not their own data. Passed down
 *  as one prop rather than via context so a block can be rendered on its own in a
 *  test. */
export interface RenderCtx {
  /** sourceId → footnote number, for `public` claims. */
  footnotes: Map<string, number>;
  sources: Source[];
  /** Namespaces element ids, so the web article, the doc pages and the hidden
   *  measuring pass can be mounted at once without colliding. */
  idPrefix: string;
  /** Jump to a source in the list and highlight it. */
  onFootnote?: (sourceId: string) => void;
  /** Open an email or meeting source in the Emails or Calls tab. */
  onOpenSource?: (source: Source) => void;
}

/**
 * Footnote numbers are not stored. Public sources are numbered by their order in
 * `sources`, so the number a claim carries is derived here and nowhere else —
 * reordering `sources` renumbers every footnote, which is why the generator must
 * keep that array stable across versions.
 */
export function footnoteNumbers(sources: Source[]): Map<string, number> {
  const numbers = new Map<string, number>();
  let n = 0;
  for (const source of sources) {
    if (source.kind === 'public') numbers.set(source.id, ++n);
  }
  return numbers;
}

/** Word count against the contract's limits. Used by the render tests, and the
 *  one place that defines "word" for this feature. */
export function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}
